import { observer } from "mobx-react-lite";
import { Table, Input, Dropdown, TextArea, Form } from "semantic-ui-react";
import { useStore } from "../../../app/stores/store";

// Define the Reel type, omitting totalDurationInMins
interface Reel {
  reelNo: string;
  startFrame: number | string;
  endFrame: number | string;
  totalFrame: number | string;
  totalDurationInMins: string;
  scanningQCRemarks: string;
}

// Define props for the component
interface ReelTableProps {
  reels: Reel[];
}

const ReelCreationDynamicTable = ({ reels }: ReelTableProps) => {
  const { viewStore } = useStore();

  // Handle dynamic input changes
  const handleChange = (name: string, value: string | number, index: number) => {
    const rowsInput = [...reels];
    rowsInput[index] = {
      ...rowsInput[index],
      [name]: value,
    };
    
    const updateFrame = () => {
      const start = parseInt(rowsInput[index]['startFrame'].toString() ?? '0', 10);
      const end = parseInt(rowsInput[index]['endFrame'].toString() ?? '0', 10);
      let total = end - start + 1;
    
      // If total is negative or NaN, set to 0
      if (isNaN(total) || total < 0) {
        total = 0;
      }
    
      rowsInput[index] = {
        ...rowsInput[index],
        totalFrame: String(total),
      };
    };
    
    if (name === 'endFrame' || name === 'startFrame') {
      updateFrame();
    }
    
    viewStore.HandleReelCreationUpdate(rowsInput);
  };

  const handleReelNoChange = (index: number, values: string[]) => {
    const concatenatedValues = values.join(",");
    const rowsInput = [...reels];
    rowsInput[index].reelNo = concatenatedValues;
    viewStore.HandleReelCreationUpdate(rowsInput);
  };

  // Generate dropdown options from 1 to n
  const staticOptions = Array.from({ length: 100 }, (_, i) => {
    const value = (i + 1).toString();
    return {
      key: value,
      text: value,
      value: value,
    };
  });
  
  return (
    <Table>
      <Table.Header>
        <Table.Row>
          <Table.HeaderCell>Reel No</Table.HeaderCell>
          <Table.HeaderCell>Start Frame</Table.HeaderCell>
          <Table.HeaderCell>End Frame</Table.HeaderCell>
          <Table.HeaderCell>Total Frames</Table.HeaderCell>
          <Table.HeaderCell>Scanning QC Remarks</Table.HeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {reels.map(({ totalDurationInMins, ...filteredReel }, index) => (
          <Table.Row key={filteredReel.reelNo}>
            {Object.keys(filteredReel).map((header) => (
              <Table.Cell key={header}>
                {['startFrame', 'endFrame', 'totalFrame'].includes(header) ? (
                  <Input
                    type={header === 'scanningQCRemarks' ? 'text' : 'number'}
                    value={(filteredReel as Record<string, any>)[header]}
                    onChange={(e) =>
                      handleChange(header, e.target.value, index)
                    }
                    readOnly={header === 'totalFrame'}
                    fluid
                  />
                ) : header === 'scanningQCRemarks' ? (
                  <Form>
                    <TextArea
                      value={(filteredReel as Record<string, any>)[header]}
                      onChange={(e) =>
                        handleChange(header, e.target.value, index)
                      }
                    />
                  </Form>
                ) : (
                  <Dropdown
                    placeholder="Select Reel No"
                    fluid
                    multiple
                    selection
                    search
                    options={staticOptions}
                    value={(filteredReel as Record<string, any>)[header].split(
                      ',',
                    )}
                    onChange={(e, data: any) =>
                      handleReelNoChange(index, data.value)
                    }
                  />
                )}
              </Table.Cell>
            ))}
          </Table.Row>
        ))}
      </Table.Body>
    </Table>
  );
};

export default observer(ReelCreationDynamicTable);
